import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Card, Chip, Text } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { ReviewTemplate, TemplateModalMode } from '../AdminDashboard.types';

interface AdminTemplateCardProps {
  template: ReviewTemplate;
  onManageVendors: (template: ReviewTemplate) => void;
  onOpenTemplateModal: (mode: TemplateModalMode, template: ReviewTemplate) => void;
  onDeleteTemplate: (templateId: number) => void;
}

export const AdminTemplateCard: React.FC<AdminTemplateCardProps> = ({
  template,
  onManageVendors,
  onOpenTemplateModal,
  onDeleteTemplate,
}) => (
  <Card key={template.id} style={styles.itemCard}>
    <Card.Content>
      <View style={styles.itemCardHeader}>
        <View style={styles.itemInfo}>
          <Text style={styles.itemName}>{template.name}</Text>
          <Text style={styles.itemDescription}>Type: {template.type}</Text>
          {template.description && (
            <Text style={styles.itemDescription}>{template.description}</Text>
          )}
          <View style={styles.itemMeta}>
            <Text style={styles.itemMetaText}>Version: {template.version}</Text>
            <Text style={styles.itemMetaText}>Status: {template.isActive ? 'Active' : 'Inactive'}</Text>
            <Text style={styles.itemMetaText}>
              Vendors: {template.vendors?.length || 0}
            </Text>
          </View>
          {template.vendors && template.vendors.length > 0 && (
            <View style={styles.vendorChips}>
              {template.vendors.slice(0, 3).map(vendor => (
                <Chip key={vendor.id} style={styles.vendorChip} textStyle={styles.vendorChipText}>
                  {vendor.name}
                </Chip>
              ))}
              {template.vendors.length > 3 && (
                <Chip style={styles.vendorChip} textStyle={styles.vendorChipText}>
                  +{template.vendors.length - 3} more
                </Chip>
              )}
            </View>
          )}
        </View>
        <View style={styles.itemActions}>
          <Button
            mode="outlined"
            onPress={() => onManageVendors(template)}
            style={[styles.actionButton, { marginBottom: spacing.xs }]}
          >
            Manage Vendors
          </Button>
          <Button
            mode="outlined"
            onPress={() => onOpenTemplateModal('edit', template)}
            style={[styles.actionButton, { marginBottom: spacing.xs }]}
          >
            Edit
          </Button>
          <Button
            mode="outlined"
            onPress={() => onOpenTemplateModal('clone', template)}
            style={[styles.actionButton, { marginBottom: spacing.xs }]}
          >
            Clone
          </Button>
          {template.isActive && (
            <Button
              mode="outlined"
              onPress={() => onDeleteTemplate(template.id)}
              style={[styles.actionButton, { borderColor: colors.error, backgroundColor: colors.error + '10' }]}
            >
              <Text style={{ color: colors.error }}>Delete</Text>
            </Button>
          )}
        </View>
      </View>
    </Card.Content>
  </Card>
);

const styles = StyleSheet.create({
  itemCard: {
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.gray300,
  },
  itemCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  itemName: {
    ...typography.h3,
    marginBottom: spacing.xs,
  },
  itemDescription: {
    ...typography.body2,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  itemMeta: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  itemMetaText: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  itemActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  actionButton: {
    marginBottom: spacing.xs,
  },
  vendorChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  vendorChip: {
    backgroundColor: colors.primary + '15',
  },
  vendorChipText: {
    fontSize: 12,
    color: colors.primary,
  },
});

export default AdminTemplateCard;
